import { useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  Announcements,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS as DndCSS,
} from '@dnd-kit/utilities';
import { AspirationCard } from './AspirationCard';
import { Aspiration } from '@/types/aspirations';
import { cn } from '@/utils/cn';

// Create accessibility announcements for screen readers during keyboard reordering
const getAnnouncements = (aspirations: Aspiration[]): Announcements => ({
  onDragStart({ active }) {
    const item = aspirations.find(a => a.id === active.id);
    return `Started reordering ${item?.title || 'item'}. Use Up and Down arrow keys to change position.`;
  },
  onDragOver({ active, over }) {
    if (over) {
      const activeItem = aspirations.find(a => a.id === active.id);
      const overItem = aspirations.find(a => a.id === over.id);
      const overIndex = aspirations.findIndex(a => a.id === over.id);
      return `${activeItem?.title || 'Item'} is now at position ${overIndex + 1}, above ${overItem?.title || 'item'}.`;
    }
    return undefined;
  },
  onDragEnd({ active, over }) {
    const item = aspirations.find(a => a.id === active.id);
    if (over && active.id !== over.id) {
      const newIndex = aspirations.findIndex(a => a.id === over.id) + 1;
      return `Finished reordering. ${item?.title || 'Item'} is now priority ${newIndex}.`;
    }
    return `Reordering completed. ${item?.title || 'Item'} position unchanged.`;
  },
  onDragCancel({ active }) {
    const item = aspirations.find(a => a.id === active.id);
    return `Reordering cancelled. ${item?.title || 'Item'} returned to original position.`;
  },
});

interface SortableAspirationCardProps {
  aspiration: Aspiration;
  onEdit: (aspiration: Aspiration) => void;
  onDelete: (aspiration: Aspiration) => void;
  onClick: (aspiration: Aspiration) => void;
}

function SortableAspirationCard({ aspiration, onEdit, onDelete, onClick }: SortableAspirationCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id: aspiration.id });

  const style = {
    transform: DndCSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative group",
        isSorting && "ring-2 ring-primary ring-offset-2"
      )}
    >
      <AspirationCard
        aspiration={aspiration}
        onEdit={onEdit}
        onDelete={onDelete}
        onClick={onClick}
        isDragging={isDragging}
        dragHandleProps={{
          ...attributes,
          ...listeners,
          'aria-pressed': isDragging,
        }}
        className={cn(
          isDragging && "opacity-50 transform rotate-1 shadow-lg"
        )}
      />
    </div>
  );
}

interface AspirationsListProps {
  aspirations: Aspiration[];
  onEdit: (aspiration: Aspiration) => void;
  onDelete: (aspiration: Aspiration) => void;
  onClick: (aspiration: Aspiration) => void;
  onReorder: (aspirations: Aspiration[]) => void;
}

export function AspirationsList({
  aspirations,
  onEdit,
  onDelete,
  onClick,
  onReorder
}: AspirationsListProps) {
  // Memoize announcements to prevent unnecessary re-renders
  const announcements = useMemo(() => getAnnouncements(aspirations), [aspirations]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    console.log('🔄 Drag End Event:', { activeId: active.id, overId: over?.id });

    if (over && active.id !== over.id) {
      const oldIndex = aspirations.findIndex(item => item.id === active.id);
      const newIndex = aspirations.findIndex(item => item.id === over.id);
      
      console.log('📊 Drag Details:', {
        activeId: active.id,
        overId: over.id,
        oldIndex,
        newIndex,
        originalOrder: aspirations.map(a => ({ id: a.id, title: a.title, priority: a.priority }))
      });

      // Create visual reorder for immediate UI feedback
      const visuallyReordered = arrayMove(aspirations, oldIndex, newIndex);
      
      // Calculate the new priority based on surrounding items' priorities
      const draggedItem = aspirations[oldIndex];
      let newPriority: number;
      
      // Get the items before and after the drop position
      const itemBefore = newIndex > 0 ? visuallyReordered[newIndex - 1] : null;
      const itemAfter = newIndex < visuallyReordered.length - 1 ? visuallyReordered[newIndex + 1] : null;
      
      if (!itemBefore && !itemAfter) {
        // Only item in list
        newPriority = 1;
      } else if (!itemBefore) {
        // Dropped at the beginning - should get priority 1
        // All other items will shift down
        newPriority = 1;
      } else if (!itemAfter) {
        // Dropped at the end - should get the next available priority
        const maxPriority = Math.max(...aspirations.map(a => a.priority));
        newPriority = maxPriority + 1;
      } else {
        // Dropped between two items - use the priority of the item after
        // The service will handle shifting other items appropriately
        newPriority = itemAfter.priority;
      }
      
      // Update the dragged item with new priority
      const updatedDraggedItem = {
        ...draggedItem,
        priority: newPriority,
        updatedAt: new Date()
      };
      
      // Create final reordered array with updated priority
      const finalReordered = visuallyReordered.map(item => 
        item.id === draggedItem.id ? updatedDraggedItem : item
      );
      
      console.log('🎯 Priority Calculation:', {
        draggedItem: draggedItem.title,
        oldPriority: draggedItem.priority,
        newPriority,
        itemBefore: itemBefore?.title || 'none',
        itemAfter: itemAfter?.title || 'none',
        beforePriority: itemBefore?.priority || 'none',
        afterPriority: itemAfter?.priority || 'none'
      });
      
      console.log('🔀 Final Order:', 
        finalReordered.map(a => ({ id: a.id, title: a.title, priority: a.priority }))
      );

      onReorder(finalReordered);
    } else {
      console.log('❌ No reorder needed:', { 
        sameItem: active.id === over?.id, 
        noOver: !over 
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable: 'Press Space to start reordering this item. Use Up and Down arrow keys to move it. Press Space to confirm or Escape to cancel.',
        },
      }}
    >
      <SortableContext 
        items={aspirations.map(a => a.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="stack-loose">
          {aspirations.map(aspiration => (
            <SortableAspirationCard
              key={aspiration.id}
              aspiration={aspiration}
              onEdit={onEdit}
              onDelete={onDelete}
              onClick={onClick}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}